import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Paper,
  Typography,
} from '@mui/material';
import { format } from 'date-fns';
import { useEffect, useState } from 'react';

import axiosInstance from '../../services/axiosConfig';

interface Message {
  id: string;
  subject?: string | null;
  body?: string | null;
  created_at?: string | null;
  is_inbound?: boolean;
  author?: {
    email?: string | null;
    first_name?: string | null;
    last_name?: string | null;
  } | null;
  recipients?: Array<{
    name?: string | null;
    handle?: string | null;
  }>;
  attachments?: Array<{
    id: string;
    filename: string;
    content_type: string;
    size: number;
  }>;
}

interface ConversationDetails {
  conversation: {
    id: string;
    subject?: string | null;
    status?: string | null;
  };
  messages: Message[];
}

interface ConversationThreadProps {
  matterId?: string | null;
  conversationId: string;
}

export const ConversationThread: React.FC<ConversationThreadProps> = ({
  matterId,
  conversationId,
}) => {
  const [loading, setLoading] = useState(false);
  const [conversation, setConversation] = useState<ConversationDetails | null>(
    null,
  );
  const [expandedMessages, setExpandedMessages] = useState<Set<string>>(
    new Set(),
  );
  const [showQuotedText, setShowQuotedText] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!conversationId) return;

    const fetchConversation = async () => {
      try {
        setLoading(true);
        setError(null);

        // Use matter-specific endpoint if matter ID provided, otherwise use sandbox endpoint
        const url = matterId
          ? `/api/matters/${matterId}/front/conversations/${conversationId}/`
          : `/api/zaddy/email-matching/conversations/${conversationId}/`;

        const response = await axiosInstance.get(url);

        setConversation(response.data);

        // Expand only the last (most recent) message by default
        if (response.data.messages && response.data.messages.length > 0) {
          const lastMessageId =
            response.data.messages[response.data.messages.length - 1].id;
          setExpandedMessages(new Set([lastMessageId]));
        }
      } catch (err: any) {
        setError(err.response?.data?.error || 'Failed to load conversation');
      } finally {
        setLoading(false);
      }
    };

    fetchConversation();
  }, [conversationId, matterId]);

  const toggleMessageExpanded = (messageId: string) => {
    setExpandedMessages((prev) => {
      const next = new Set(prev);
      if (next.has(messageId)) {
        next.delete(messageId);
      } else {
        next.add(messageId);
      }
      return next;
    });
  };

  const toggleQuotedText = (messageId: string) => {
    setShowQuotedText((prev) => {
      const next = new Set(prev);
      if (next.has(messageId)) {
        next.delete(messageId);
      } else {
        next.add(messageId);
      }
      return next;
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Paper sx={{ p: 3, bgcolor: 'error.light' }}>
        <Typography color="error">{error}</Typography>
      </Paper>
    );
  }

  if (!conversation) {
    return null;
  }

  return (
    <Box sx={{ maxWidth: '100%' }}>
      {/* Conversation Header */}
      <Paper sx={{ p: 3, mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Box>
            <Typography variant="h6" gutterBottom>
              {conversation.conversation.subject || 'Conversation'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {conversation.messages.length} message
              {conversation.messages.length !== 1 ? 's' : ''}
            </Typography>
          </Box>
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              window.open(
                `https://app.frontapp.com/open/${conversationId}`,
                '_blank',
              );
            }}
          >
            Open in Front →
          </Button>
        </Box>
      </Paper>

      {/* Messages */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {conversation.messages.map((msg, index) => {
          const isExpanded = expandedMessages.has(msg.id);
          const isLast = index === conversation.messages.length - 1;
          const authorName = msg.author
            ? `${msg.author.first_name || ''} ${msg.author.last_name || ''}`.trim() ||
              msg.author.email ||
              'Unknown'
            : 'Unknown';

          return (
            <Paper
              key={msg.id}
              sx={{
                overflow: 'hidden',
                borderLeft: 4,
                borderLeftColor: msg.is_inbound ? 'primary.main' : 'grey.300',
              }}
            >
              {/* Message Header */}
              <Box
                sx={{
                  p: 2,
                  bgcolor:
                    isExpanded || isLast ? 'background.paper' : 'grey.50',
                  cursor: isLast ? 'default' : 'pointer',
                  '&:hover': isLast ? {} : { bgcolor: 'grey.100' },
                }}
                onClick={() => !isLast && toggleMessageExpanded(msg.id)}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'start',
                  }}
                >
                  <Box>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {authorName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {msg.created_at
                        ? format(new Date(msg.created_at), 'MMM d, yyyy h:mm a')
                        : ''}
                      {msg.recipients && msg.recipients.length > 0 && (
                        <span>
                          {' '}
                          • to{' '}
                          {msg.recipients
                            .map((r) => r.name || r.handle)
                            .join(', ')}
                        </span>
                      )}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        bgcolor: msg.is_inbound ? 'primary.main' : 'grey.400',
                        color: 'white',
                        px: 1,
                        py: 0.5,
                        borderRadius: 1,
                        fontWeight: 'bold',
                        fontSize: '0.65rem',
                      }}
                    >
                      {msg.is_inbound ? 'INBOUND' : 'OUTBOUND'}
                    </Typography>
                    {!isLast && (
                      <Typography variant="caption" color="text.secondary">
                        {isExpanded ? '▼' : '▶'}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Box>

              {/* Message Body */}
              {(isExpanded || isLast) && (
                <Box sx={{ p: 2 }}>
                  {msg.subject && (
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      {msg.subject}
                    </Typography>
                  )}

                  {msg.body &&
                    (() => {
                      const isQuotedShown = showQuotedText.has(msg.id);

                      // Split body into main content and quoted text
                      const parser = new DOMParser();
                      const doc = parser.parseFromString(msg.body, 'text/html');

                      // Find and remove quoted sections
                      const quotedElements = doc.querySelectorAll(
                        'blockquote, .gmail_quote, [class*="quote"]',
                      );
                      const hasQuotes = quotedElements.length > 0;

                      if (!isQuotedShown && hasQuotes) {
                        quotedElements.forEach((el) => el.remove());
                      }

                      const cleanedBody = doc.body.innerHTML;

                      return (
                        <>
                          <Box
                            sx={{
                              '& img': {
                                maxWidth: '100%',
                                height: 'auto',
                              },
                              '& a': {
                                color: 'primary.main',
                                textDecoration: 'underline',
                              },
                              fontSize: '0.875rem',
                              lineHeight: 1.6,
                            }}
                            dangerouslySetInnerHTML={{
                              __html: cleanedBody,
                            }}
                          />
                          {hasQuotes && (
                            <Button
                              size="small"
                              onClick={() => toggleQuotedText(msg.id)}
                              sx={{ mt: 1 }}
                            >
                              {isQuotedShown
                                ? '▲ Hide quoted text'
                                : '▼ Show quoted text'}
                            </Button>
                          )}
                        </>
                      );
                    })()}

                  {msg.attachments && msg.attachments.length > 0 && (
                    <>
                      <Divider sx={{ my: 2 }} />
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontWeight: 'bold', mb: 1, display: 'block' }}
                      >
                        Attachments ({msg.attachments.length})
                      </Typography>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 1,
                        }}
                      >
                        {msg.attachments.map((att) => {
                          const isPDF =
                            att.content_type === 'application/pdf' ||
                            att.filename?.toLowerCase().endsWith('.pdf');

                          return (
                            <Box
                              key={att.id}
                              sx={{
                                p: 1.5,
                                border: 1,
                                borderColor: 'divider',
                                borderRadius: 1,
                                bgcolor: 'grey.50',
                              }}
                            >
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1,
                                  mb: 1,
                                }}
                              >
                                <Typography variant="body2">
                                  {isPDF ? '📄' : '📎'}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{ flex: 1, fontWeight: 'medium' }}
                                >
                                  {att.filename}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {Math.round(att.size / 1024)}KB
                                </Typography>
                              </Box>
                              <Button
                                size="small"
                                variant="outlined"
                                href={
                                  matterId
                                    ? `${import.meta.env.VITE_APP_DJANGO_BACKEND_HOST}/api/matters/${matterId}/front/messages/${msg.id}/attachments/${att.id}/download`
                                    : `${import.meta.env.VITE_APP_DJANGO_BACKEND_HOST}/api/zaddy/email-matching/messages/${msg.id}/attachments/${att.id}/download`
                                }
                                download
                              >
                                Download
                              </Button>
                            </Box>
                          );
                        })}
                      </Box>
                    </>
                  )}
                </Box>
              )}
            </Paper>
          );
        })}
      </Box>
    </Box>
  );
};
